import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Static imports — Vite bundles them at build time, eliminating HTTP waterfall
import enCommon from "@/locales/en/common.json";
import enAuth from "@/locales/en/auth.json";
import enDashboard from "@/locales/en/dashboard.json";
import enResources from "@/locales/en/resources.json";
import enBilling from "@/locales/en/billing.json";
import enOrders from "@/locales/en/orders.json";
import enSettings from "@/locales/en/settings.json";
import enValidation from "@/locales/en/validation.json";
import enErrors from "@/locales/en/errors.json";
import enNotifications from "@/locales/en/notifications.json";
import enDialogs from "@/locales/en/dialogs.json";
import enDomains from "@/locales/en/domains.json";
import enEInvoice from "@/locales/en/e-invoice.json";
import enBillingInfo from "@/locales/en/billing-info.json";

import viCommon from "@/locales/vi/common.json";
import viAuth from "@/locales/vi/auth.json";
import viDashboard from "@/locales/vi/dashboard.json";
import viResources from "@/locales/vi/resources.json";
import viBilling from "@/locales/vi/billing.json";
import viOrders from "@/locales/vi/orders.json";
import viSettings from "@/locales/vi/settings.json";
import viValidation from "@/locales/vi/validation.json";
import viErrors from "@/locales/vi/errors.json";
import viNotifications from "@/locales/vi/notifications.json";
import viDialogs from "@/locales/vi/dialogs.json";
import viDomains from "@/locales/vi/domains.json";
import viEInvoice from "@/locales/vi/e-invoice.json";
import viBillingInfo from "@/locales/vi/billing-info.json";

export const supportedLanguages = ["en", "vi"] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const namespaces = [
  "common",
  "auth",
  "dashboard",
  "resources",
  "billing",
  "orders",
  "settings",
  "validation",
  "errors",
  "notifications",
  "dialogs",
  "domains",
  "e-invoice",
  "billing-info",
] as const;
export type Namespace = (typeof namespaces)[number];

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    resources: enResources,
    billing: enBilling,
    orders: enOrders,
    settings: enSettings,
    validation: enValidation,
    errors: enErrors,
    notifications: enNotifications,
    dialogs: enDialogs,
    domains: enDomains,
    "e-invoice": enEInvoice,
    "billing-info": enBillingInfo,
  },
  vi: {
    common: viCommon,
    auth: viAuth,
    dashboard: viDashboard,
    resources: viResources,
    billing: viBilling,
    orders: viOrders,
    settings: viSettings,
    validation: viValidation,
    errors: viErrors,
    notifications: viNotifications,
    dialogs: viDialogs,
    domains: viDomains,
    "e-invoice": viEInvoice,
    "billing-info": viBillingInfo,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Vietnamese is the product default — a first-time visitor with no saved
    // preference lands on "vi" regardless of browser/OS locale, not "en".
    fallbackLng: "vi",
    supportedLngs: [...supportedLanguages],
    load: "languageOnly",
    defaultNS: "common",
    ns: [...namespaces],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Only ever trust an explicit prior choice — no navigator/htmlTag
      // auto-detection, or the browser's locale would override the "vi" default.
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
    },
    react: {
      useSuspense: false,
    },
  });

export default i18n;
